import { z } from 'zod';

const requiredText = (label: string) =>
  z.string().trim().min(1, `${label} is required.`);

export const quoteFormSchema = z
  .object({
    service: requiredText('Service requested'),
    type: z.number().int().positive().nullable(),
    company: requiredText('Company'),
    mcNumber: z
      .string()
      .trim()
      .regex(/^\d*$/, 'MC number must contain only digits.'),
    name: requiredText('Your name'),
    email: z
      .string()
      .trim()
      .min(1, 'Email address is required.')
      .email('Enter a valid email address.'),
    phone: z
      .string()
      .trim()
      .min(1, 'Phone number is required.')
      .refine(
        (value) => value.replace(/\D/g, '').length >= 7,
        'Enter a valid phone number.'
      ),
    origin: z.string(),
    destination: z.string(),
    pickupDate: z.date().nullable(),
    deliveryDate: z.date().nullable(),
    freightDescription: requiredText('Freight description'),
    weight: z.string(),
    dimensions: z.string(),
    pieces: z.string(),
    equipment: z.string(),
    regulatedMaterials: z.string(),
    accessorials: z.string(),
    subject: z.string(),
    message: z.string(),
    contactMethod: z.number().int().positive().nullable(),
    preferredTime: z.date().nullable(),
    warehouseLocation: z.string(),
    storageType: z.string(),
    storageDuration: z.string(),
    estimatedSpace: z.string(),
    palletUnits: z.string(),
    requestedStartDate: z.date().nullable(),
    inboundFrequency: z.string(),
    warehouseCustomerType: z.string(),
    specialHandling: z.array(z.string()),
    valueAddedServices: z.array(z.string()),
  })
  .superRefine((form, context) => {
    if (form.service === 'warehousing') {
      const warehouseFields: Array<
        [
          keyof Pick<
            typeof form,
            | 'warehouseLocation'
            | 'storageType'
            | 'storageDuration'
            | 'estimatedSpace'
            | 'warehouseCustomerType'
          >,
          string,
        ]
      > = [
        ['warehouseLocation', 'Warehouse location is required.'],
        ['storageType', 'Storage type is required.'],
        ['storageDuration', 'Storage duration is required.'],
        ['estimatedSpace', 'Estimated space is required.'],
        ['warehouseCustomerType', 'Customer type is required.'],
      ];

      warehouseFields.forEach(([field, message]) => {
        if (!form[field].trim()) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            message,
          });
        }
      });

      if (!form.requestedStartDate) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['requestedStartDate'],
          message: 'Requested start date is required.',
        });
      }
      return;
    }

    if (!form.type) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['type'],
        message: 'Select what best describes you.',
      });
    }

    const freightFields: Array<
      [keyof Pick<typeof form, 'origin' | 'destination' | 'equipment'>, string]
    > = [
      ['origin', 'Origin state is required.'],
      ['destination', 'Destination state is required.'],
      ['equipment', 'Equipment type is required.'],
    ];

    freightFields.forEach(([field, message]) => {
      if (!form[field].trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message,
        });
      }
    });

    if (!form.pickupDate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pickupDate'],
        message: 'Requested pickup date is required.',
      });
    }

    if (!form.deliveryDate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['deliveryDate'],
        message: 'Requested delivery date is required.',
      });
    } else if (form.pickupDate && form.deliveryDate < form.pickupDate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['deliveryDate'],
        message: 'Delivery date cannot be earlier than the pickup date.',
      });
    }

    const weight = Number(form.weight);
    if (!form.weight.trim() || !Number.isFinite(weight) || weight <= 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['weight'],
        message: 'Enter an approximate weight greater than 0.',
      });
    }
  });

export type QuoteFormValues = z.infer<typeof quoteFormSchema>;
